/* THE LIFECYCLE STRIP. Nine cells, and the arrows between them are a READING ORDER,
 * not a flow.
 *
 * The stages OVERLAP: a person is Member and Active and At Risk at the same moment
 * (SITE-RULES 11a). That is why this is not a funnel, why nothing on any console is
 * computed off it, and why the note saying so is on the strip itself rather than in a
 * comment nobody reading the screen will see.
 *
 * DOWN IS NOT AUTOMATICALLY BAD. Fewer at risk, fewer churned and a smaller win-back
 * pool are the three cells where a fall is the good news, so the colour follows the
 * meaning and not the sign.
 *
 * EVERY CELL OPENS ITS PEOPLE (SITE-RULES 11e). A count nobody can open is the dead
 * end the names rule exists to prevent.
 */
import { useState } from 'react';
import { useAsync, useSource } from '../../data/SourceProvider.js';
import type { JourneyCell } from '../../data/contract.js';
import { NoneRow } from '../../components/Panel.js';
import { Modal } from '../../components/Modal.js';
import { Avatar } from '../../components/Avatar.js';
import { fmt } from '../../lib/fmt.js';
import { go } from '../../routes.js';

export function LifecycleStrip() {
  const source = useSource();
  const { data } = useAsync(() => source.getJourney(), [source]);
  const [open, setOpen] = useState<JourneyCell | null>(null);
  if (!data) return null;

  return (
    <section className="panel lcstrip">
      <div className="lchead">
        <div>
          <div className="eyebrow t-label-xsmall">One journey, one set of words</div>
          <h2 className="t-heading-h3">Member and lead journey</h2>
        </div>
        <p className="t-caption muted lcnote">
          Change is against the same board {data[0]?.windowDays ?? 7} days ago.
        </p>
      </div>

      <div className="lcrow">
        {data.map((c, i) => {
          const falling = c.diff < 0;
          const better = c.good === 'down' ? falling : !falling;
          const shown = c.delta == null ? Math.abs(c.diff) : Math.abs(c.delta);
          const title = `${c.was} ${c.windowDays} days ago, ${c.n} today`;
          return (
            <div className="lcpair" key={c.key}>
              {i > 0 && <div className="lcarrow" aria-hidden="true">→</div>}
              <button
                type="button" className={'lccell' + (c.tone ? ' ' + c.tone : '')}
                disabled={!c.opens}
                title={c.opens ? `Show the ${c.n} people at ${c.label}` : undefined}
                onClick={() => setOpen(c)}
              >
                <span className="lcn t-heading-h2">{fmt.num(c.n)}</span>
                <span className="lcl t-caption">{c.label}</span>
                {/* Was empty, still empty: say nothing rather than a fabricated 0%. */}
                {(c.quiet || c.diff === 0)
                  ? <span className="lcd flat t-caption" title={title}>no change</span>
                  : <span className={'lcd t-caption' + (better ? '' : ' down')} title={title}>
                      {falling ? '↓ ' : '↑ '}{shown}{c.unit}
                    </span>}
              </button>
            </div>
          );
        })}
      </div>

      {open && <CellPeopleModal c={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

const CELL_PAGE = 25;

function CellPeopleModal({ c, onClose }: { c: JourneyCell; onClose: () => void }) {
  const source = useSource();
  const [page, setPage] = useState(1);
  const { data } = useAsync(
    () => source.getJourneyCellPeople(c.key, { page, size: CELL_PAGE }), [source, c.key, page]);

  /* Leads and members are two lists, not one: a cell can hold both kinds and neither is
     a slice of the other, so the pager walks them together and the total is the sum. */
  const total = (data?.members.total ?? 0) + (data?.leads.total ?? 0);
  const shown = (data?.members.items.length ?? 0) + (data?.leads.items.length ?? 0);
  const from = (page - 1) * CELL_PAGE + 1;

  return (
    <Modal
      title={`${c.label} · ${fmt.num(c.n)}`}
      onClose={onClose}
      footer={<button type="button" className="btn t-label-medium" onClick={onClose}>Done</button>}
    >
      {!data ? <NoneRow>Reading…</NoneRow>
        : !total ? <NoneRow>Nobody is at this stage right now.</NoneRow>
        : (
          <>
            <div className="rows register">
              {data.leads.items.map((l) => (
                <button key={l.id} type="button" className="row clickable"
                  onClick={() => { onClose(); go('/leads/' + l.id); }}>
                  <Avatar name={l.name} photoUrl={l.photoUrl} size="md" />
                  <div className="who">
                    <div className="n t-label-large">{l.name}</div>
                    <div className="s t-caption muted">Lead · enquired {fmt.ago(l.createdAt)}</div>
                  </div>
                  <span className="t-label-medium muted">→</span>
                </button>
              ))}
              {data.members.items.map((m) => (
                <button key={m.id} type="button" className="row clickable"
                  onClick={() => { onClose(); go('/members/' + m.id); }}>
                  <Avatar name={m.name} photoUrl={m.photoUrl} size="md" />
                  <div className="who">
                    <div className="n t-label-large">{m.name}</div>
                    <div className="s t-caption muted">
                      {[m.plan, m.lastVisit ? 'last visit ' + fmt.ago(m.lastVisit) : 'no visit on record']
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="t-label-medium muted">→</span>
                </button>
              ))}
            </div>

            {total > CELL_PAGE && (
              <div className="pager t-label-small">
                <button type="button" className="btn" disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}>Previous</button>
                <span className="muted">{from}–{from + shown - 1} of {fmt.num(total)}</span>
                <button type="button" className="btn" disabled={page * CELL_PAGE >= total}
                  onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
    </Modal>
  );
}
